// USPS 3-digit ZIP prefix ranges -> state code; Shopify's rate endpoints reject US lookups without a province.
const US_ZIP_PREFIX_STATES = [
  [5, 5, "NY"], [6, 7, "PR"], [8, 8, "VI"], [9, 9, "PR"], [10, 27, "MA"], [28, 29, "RI"],
  [30, 38, "NH"], [39, 49, "ME"], [50, 59, "VT"], [60, 69, "CT"], [70, 89, "NJ"], [90, 98, "AE"],
  [100, 149, "NY"], [150, 196, "PA"], [197, 199, "DE"], [200, 200, "DC"], [201, 201, "VA"],
  [202, 205, "DC"], [206, 219, "MD"], [220, 246, "VA"], [247, 268, "WV"], [270, 289, "NC"],
  [290, 299, "SC"], [300, 319, "GA"], [320, 339, "FL"], [340, 340, "AA"], [341, 349, "FL"],
  [350, 369, "AL"], [370, 385, "TN"], [386, 397, "MS"], [398, 399, "GA"], [400, 427, "KY"],
  [430, 459, "OH"], [460, 479, "IN"], [480, 499, "MI"], [500, 528, "IA"], [530, 549, "WI"],
  [550, 567, "MN"], [569, 569, "DC"], [570, 577, "SD"], [580, 588, "ND"], [590, 599, "MT"],
  [600, 629, "IL"], [630, 658, "MO"], [660, 679, "KS"], [680, 693, "NE"], [700, 714, "LA"],
  [716, 729, "AR"], [730, 732, "OK"], [733, 733, "TX"], [734, 749, "OK"], [750, 799, "TX"],
  [800, 816, "CO"], [820, 831, "WY"], [832, 838, "ID"], [840, 847, "UT"], [850, 865, "AZ"],
  [870, 884, "NM"], [885, 885, "TX"], [889, 898, "NV"], [900, 961, "CA"], [962, 966, "AP"],
  [967, 968, "HI"], [969, 969, "GU"], [970, 979, "OR"], [980, 994, "WA"], [995, 999, "AK"],
];

const usProvinceFromZip = (zip) => {
  const match = /^\s*(\d{3})/.exec(String(zip));
  if (!match) return "";
  const prefix = Number(match[1]);
  const range = US_ZIP_PREFIX_STATES.find(([from, to]) => prefix >= from && prefix <= to);
  return range ? range[2] : "";
};

const US_COUNTRY_CODE = "US";
const US_COUNTRY_NAME = "United States";

// HF-381: Calcurates answers every rate, so the async pair Shopify recommends for slow
// carriers replaces /cart/shipping_rates.json, which is throttled under repeated calls.
const SHIPPING_POLL_INTERVAL_MS = 500;
const SHIPPING_POLL_TIMEOUT_MS = 15000;

// A ZIP the shopper typed here; it outranks the header's for the rest of the session.
const CART_SHIPPING_ZIP_KEY = "hudson:cart-shipping-zip";

const readCartShippingZip = () => {
  try {
    return sessionStorage.getItem(CART_SHIPPING_ZIP_KEY) || "";
  } catch (err) {
    return "";
  }
};

const writeCartShippingZip = (zip) => {
  try {
    if (zip) sessionStorage.setItem(CART_SHIPPING_ZIP_KEY, zip);
    else sessionStorage.removeItem(CART_SHIPPING_ZIP_KEY);
  } catch (err) {
    // Private mode: the typed ZIP just lives as long as the field does.
  }
};

// One-way: the header ZIP is read here and never written back.
const readHeaderZip = () => window.HudsonLocation?.get()?.zip || "";

const waitFor = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal.reason);
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });

const fetchShippingRates = async (query, signal) => {
  const root = window.Shopify.routes.root;
  const prepared = await fetch(`${root}cart/prepare_shipping_rates.json?${query}`, { method: "POST", signal });
  if (!prepared.ok) throw new Error(await prepared.text());

  const deadline = Date.now() + SHIPPING_POLL_TIMEOUT_MS;
  while (Date.now() < deadline) {
    const response = await fetch(`${root}cart/async_shipping_rates.json?${query}`, { signal });
    if (!response.ok) throw new Error(await response.text());
    // `null` means the carrier is still calculating.
    const data = await response.json();
    if (data) return data.shipping_rates || [];
    await waitFor(SHIPPING_POLL_INTERVAL_MS, signal);
  }
  throw new Error("Shipping rates timed out");
};

// Shared across instances, because the drawer rebuilds this element on every cart change.
// The key carries the cart fingerprint (the drawer's guard: Athos adds to cart without
// publishing cartUpdate) and a generation bumped on cartUpdate (the cart page's guard: it
// keeps its element). A request that outlives a cart update writes under the old
// generation, which nothing reads again.
const shippingRatesCache = new Map();
let shippingCacheGeneration = 0;
if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    shippingCacheGeneration += 1;
    shippingRatesCache.clear();
  });
}

// Applying a discount re-renders the cart, which replaces this element. The
// panel to restore is handed to the instance that comes back, so the popup the
// customer is looking at survives the swap instead of vanishing under them.
const PENDING_PANEL_TTL_MS = 5000;
let pendingPanel = null;
let pendingPanelTimer = null;

const rememberPanel = (panel) => {
  pendingPanel = panel;
  clearTimeout(pendingPanelTimer);
  // If no re-render arrives, drop it rather than restoring a stale panel later.
  pendingPanelTimer = setTimeout(() => {
    pendingPanel = null;
  }, PENDING_PANEL_TTL_MS);
};

class CartAdditionalFeatures extends HTMLElement {
  // CSS selector for things we consider focusable inside a panel —
  // used by the focus trap when this component runs in 'drawer' (modal) context.
  static FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  connectedCallback() {
    this.triggers = this.querySelectorAll(".wt-cart-features__trigger");
    this.drawers = this.querySelectorAll(".wt-cart-features__drawer");
    this.overlay = this.querySelector("[data-overlay]");
    this.closeButtons = this.querySelectorAll("[data-close]");
    this.context = this.dataset.context || "page";
    this.shopCurrency = this.dataset.shopCurrency || "";
    this.lastTrigger = null;

    // i18n strings — provided by the snippet via `data-i18n-*` attributes.
    // English fallbacks here mean the component still works if the
    // attributes are missing (e.g. older cached HTML). Convention:
    // hyphenated `data-i18n-foo-bar` becomes `dataset.i18nFooBar`.
    const ds = this.dataset;
    this.strings = {
      shippingSelect: ds.i18nShippingSelect || "Please select a country and enter a postal code.",
      shippingCalculating: ds.i18nShippingCalculating || "Calculating…",
      shippingNoRate: ds.i18nShippingNoRate || "There is no shipping rate for this destination. Try to check the address.",
      shippingOneRate: ds.i18nShippingOneRate || "There is one shipping rate for this destination:",
      // n-rates template uses `__COUNT__` as a placeholder; we substitute at use site.
      shippingNRatesTpl: ds.i18nShippingNRates || "There are __COUNT__ shipping rates for this destination:",
      shippingFree: ds.i18nShippingFree || "Free",
      // A failed or timed-out request is not "no rate for this address"; same copy as the discount panel.
      shippingError: ds.i18nDiscountError || "Something went wrong. Please try again.",
      discountEnter: ds.i18nDiscountEnter || "Please enter a discount code.",
      discountApplying: ds.i18nDiscountApplying || "Applying…",
      discountApplied: ds.i18nDiscountApplied || "Discount applied!",
      discountInvalid: ds.i18nDiscountInvalid || "Discount code is invalid or does not apply to the current cart.",
      discountError: ds.i18nDiscountError || "Something went wrong. Please try again.",
    };

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.closeAll = this.closeAll.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.triggers.forEach((t) => t.addEventListener("click", this.onTriggerClick));
    this.closeButtons.forEach((b) => b.addEventListener("click", this.closeAll));
    if (this.overlay) this.overlay.addEventListener("click", this.closeAll);
    this.addEventListener("keydown", this.onKeyDown);

    // The whole side cart can be dismissed while a feature panel is still
    // open. Collapse it too, otherwise the next time the cart is opened the
    // panel is still expanded on its stale result.
    if (this.context === "drawer") {
      this.cartDrawerCloseEvent =
        typeof PUB_SUB_EVENTS !== "undefined" ? PUB_SUB_EVENTS.cartDrawerClose : "cart-drawer-close";
      // Focus is restored to the trigger as usual: collapsing marks the panel
      // inert, which blurs whatever is focused inside it. The cart drawer's own
      // opener restore runs a frame later and still wins when it has one.
      document.addEventListener(this.cartDrawerCloseEvent, this.closeAll);
    }

    // a11y: collapsed panels must not be focusable. `max-height: 0;
    // overflow: hidden;` (desktop accordion) and `transform: translateY(100%)`
    // (mobile/cart-drawer slide-up) both hide visually but leave children
    // tab-reachable — `inert` removes them from the a11y tree + tab order.
    this.drawers.forEach((d) => {
      if (!d.hasAttribute("open")) d.setAttribute("inert", "");
    });

    this.initNote();
    this.initShipping();
    this.initDiscount();

    if (pendingPanel && pendingPanel.context === this.context) {
      const { feature, message } = pendingPanel;
      pendingPanel = null;
      clearTimeout(pendingPanelTimer);
      this.openDrawer(feature);
      this.renderDiscountResult(message, "success");
    }
  }

  disconnectedCallback() {
    this.removeEventListener("keydown", this.onKeyDown);
    this.shippingListeners?.abort();
    this.cancelShippingRequest();
    if (this.cartDrawerCloseEvent) {
      document.removeEventListener(this.cartDrawerCloseEvent, this.closeAll);
    }
  }

  // ── Drawer toggling ─────────────────────────────────────────
  onTriggerClick(event) {
    const trigger = event.currentTarget;
    const feature = trigger.dataset.feature;
    const drawer = this.querySelector(`.wt-cart-features__drawer[data-drawer="${feature}"]`);

    // Toggle: clicking an already-open trigger collapses everything.
    // Used by the desktop accordion (cart-page ≥900px) so a second
    // click closes the panel; mobile slide-up behaves the same way.
    if (drawer && drawer.hasAttribute("open")) {
      this.closeAll();
      return;
    }

    this.openDrawer(feature);
  }

  openDrawer(feature) {
    this.closeAll();
    this.resetFeedback();

    const drawer = this.querySelector(`.wt-cart-features__drawer[data-drawer="${feature}"]`);
    if (!drawer) return;

    const trigger = this.querySelector(`.wt-cart-features__trigger[data-feature="${feature}"]`);
    // Remember which trigger opened the panel so we can return focus
    // to it on close (a11y: don't drop focus to <body>).
    this.lastTrigger = trigger || null;

    drawer.setAttribute("open", "");
    drawer.removeAttribute("inert");
    if (trigger) {
      trigger.classList.add("wt-cart-features__trigger--open");
      trigger.setAttribute("aria-expanded", "true");
    }
    this.overlay?.setAttribute("open", "");
    this.classList.add("wt-cart-features--open");

    if (feature === "shipping") this.startShippingEstimate();

    // Modal contexts (cart drawer slide-up): move focus into the panel
    // so it ends up inside the focus trap. The desktop cart-page
    // accordion keeps focus on the trigger so the trigger row reads
    // naturally as the heading.
    if (this.context === "drawer") {
      const firstField = drawer.querySelector(CartAdditionalFeatures.FOCUSABLE_SELECTOR);
      if (firstField) setTimeout(() => firstField.focus(), 50);
    }
  }

  closeAll() {
    const wasOpen = this.classList.contains("wt-cart-features--open");

    this.drawers.forEach((d) => {
      d.removeAttribute("open");
      // Re-disable interaction + tab order on every collapsed panel.
      d.setAttribute("inert", "");
    });
    this.triggers.forEach((t) => {
      t.classList.remove("wt-cart-features__trigger--open");
      t.setAttribute("aria-expanded", "false");
    });
    this.overlay?.removeAttribute("open");
    this.classList.remove("wt-cart-features--open");

    // Return focus to the trigger that opened the panel (a11y: keyboard
    // user shouldn't be dumped to <body>). Only do this if we were
    // genuinely closing something — avoid stealing focus on initial render.
    if (wasOpen && this.lastTrigger) {
      this.lastTrigger.focus();
    }
    this.lastTrigger = null;
  }

  // Feedback belongs to one open/close cycle — a reopened panel starts blank.
  resetFeedback() {
    [this.shippingResult, this.discountResult].forEach((el) => {
      if (!el) return;
      el.textContent = "";
      delete el.dataset.state;
    });
    [this.discountInput, this.shippingZip].forEach((el) => el?.removeAttribute("aria-invalid"));
  }

  onKeyDown(event) {
    if (!this.classList.contains("wt-cart-features--open")) return;

    if (event.key === "Escape") {
      this.closeAll();
      return;
    }

    // Focus trap — only meaningful in 'drawer' context where the panel is
    // a real modal (`role="dialog" aria-modal="true"`). On the cart page
    // the panel is an inline accordion / region, no trap needed.
    if (event.key === "Tab" && this.context === "drawer") {
      const openDrawer = this.querySelector(".wt-cart-features__drawer[open]");
      if (!openDrawer) return;

      const focusable = Array.from(
        openDrawer.querySelectorAll(CartAdditionalFeatures.FOCUSABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || !openDrawer.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  // ── Order note ──────────────────────────────────────────────
  initNote() {
    const textarea = this.querySelector("[data-note-textarea]");
    const saveBtn = this.querySelector("[data-note-save]");
    if (!textarea || !saveBtn) return;

    saveBtn.addEventListener("click", async () => {
      saveBtn.disabled = true;
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ note: textarea.value }),
        });
        if (!response.ok) throw new Error("Failed to save note");
        this.closeAll();
      } catch (err) {
        console.error("[cart-features note]", err);
      } finally {
        saveBtn.disabled = false;
      }
    });
  }

  // ── Estimate Shipping ───────────────────────────────────────
  initShipping() {
    const submit = this.querySelector("[data-shipping-submit]");
    if (!submit) return;

    this.shippingSubmit = submit;
    this.shippingResult = this.querySelector("[data-shipping-result]");
    this.shippingCountry = this.querySelector("[data-shipping-country]");
    this.shippingZip = this.querySelector("[data-shipping-zip]");
    this.shippingPanel = this.querySelector('.wt-cart-features__drawer[data-drawer="shipping"]');
    this.shippingAuto = this.hasAttribute("data-shipping-auto");
    this.cartFingerprint = this.dataset.cartFingerprint || "";
    this.shippingRequest = null;
    this.shippingRequestKey = "";

    submit.addEventListener("click", this.onShippingSubmit.bind(this));

    this.shippingZip?.addEventListener("input", () => {
      this.shippingZip.removeAttribute("aria-invalid");
      // A running estimate belongs to the ZIP the shopper is replacing.
      if (!this.shippingRequest) return;
      this.cancelShippingRequest();
      this.shippingResult.textContent = "";
      delete this.shippingResult.dataset.state;
    });
    // Only an edit by the shopper pins the ZIP; clearing the field hands it back to the header.
    this.shippingZip?.addEventListener("change", () => {
      writeCartShippingZip(this.shippingZip.value.trim());
    });

    this.shippingListeners = new AbortController();
    if (window.HudsonLocation) {
      document.addEventListener(window.HudsonLocation.EVENT, this.onHeaderLocationChange.bind(this), {
        signal: this.shippingListeners.signal,
      });
    }

    this.prefillShippingZip();
  }

  isUnitedStates() {
    return (
      this.shippingCountry?.dataset.countryCode === US_COUNTRY_CODE ||
      this.shippingCountry?.value === US_COUNTRY_NAME
    );
  }

  // Fills an empty field only: the shopper's own ZIP first, then the header's (US ZIPs only).
  prefillShippingZip() {
    if (!this.shippingZip || this.shippingZip.value.trim()) return;
    const headerZip = this.isUnitedStates() ? readHeaderZip() : "";
    this.shippingZip.value = readCartShippingZip() || headerZip;
  }

  startShippingEstimate() {
    this.prefillShippingZip();
    if (this.shippingAuto && this.shippingZip?.value.trim()) this.onShippingSubmit();
  }

  onHeaderLocationChange(event) {
    if (!this.shippingZip || readCartShippingZip() || !this.isUnitedStates()) return;
    // Picking a showroom re-dispatches with the same ZIP; a late geolocation must not
    // overwrite a ZIP the shopper is still typing.
    const zip = event.detail?.location?.zip || "";
    if (zip === this.shippingZip.value.trim() || document.activeElement === this.shippingZip) return;
    this.cancelShippingRequest();
    this.shippingZip.value = zip;

    if (!this.shippingPanel?.hasAttribute("open")) return;
    this.resetFeedback();
    if (this.shippingAuto && this.shippingZip.value) this.onShippingSubmit();
  }

  cancelShippingRequest() {
    this.shippingRequest?.abort();
    this.shippingRequest = null;
    this.shippingRequestKey = "";
  }

  async onShippingSubmit() {
    const country = this.shippingCountry?.value || "";
    const zip = this.shippingZip?.value.trim() || "";

    if (!country || !zip) {
      this.cancelShippingRequest();
      this.renderShippingResult(this.strings.shippingSelect, "error");
      return;
    }

    const params = new URLSearchParams({ "shipping_address[country]": country, "shipping_address[zip]": zip });
    if (this.isUnitedStates()) {
      const province = usProvinceFromZip(zip);
      if (province) params.set("shipping_address[province]", province);
    }
    const query = params.toString();
    const cacheKey = `${shippingCacheGeneration}|${this.cartFingerprint}|${query}`;

    // The button stays enabled: a new ZIP replaces the running request, the same one joins it.
    if (this.shippingRequestKey === cacheKey) {
      this.renderShippingResult(this.strings.shippingCalculating, "loading");
      return;
    }

    this.cancelShippingRequest();
    const cachedRates = shippingRatesCache.get(cacheKey);
    if (cachedRates) {
      this.renderShippingOutcome(cachedRates);
      return;
    }

    this.renderShippingResult(this.strings.shippingCalculating, "loading");
    const request = new AbortController();
    this.shippingRequest = request;
    this.shippingRequestKey = cacheKey;
    // The poll loop checks its deadline only between requests; this also cuts a fetch that stalls.
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      request.abort();
    }, SHIPPING_POLL_TIMEOUT_MS);

    try {
      const rates = await fetchShippingRates(query, request.signal);
      if (request.signal.aborted) return;
      // An empty answer can be a carrier that did not reply in time; let the next try ask again.
      if (rates.length > 0) shippingRatesCache.set(cacheKey, rates);
      this.renderShippingOutcome(rates);
    } catch (err) {
      if (request.signal.aborted && !timedOut) return;
      console.error("[cart-features shipping]", err);
      this.renderShippingResult(this.strings.shippingError, "error");
    } finally {
      clearTimeout(timeout);
      if (this.shippingRequest === request) {
        this.shippingRequest = null;
        this.shippingRequestKey = "";
      }
    }
  }

  renderShippingOutcome(rates) {
    if (rates.length === 0) {
      this.renderShippingResult(this.strings.shippingNoRate, "error");
    } else {
      this.renderShippingRates(rates);
    }
  }

  renderShippingRates(rates) {
    const intro =
      rates.length === 1
        ? this.strings.shippingOneRate
        : this.strings.shippingNRatesTpl.replace("__COUNT__", String(rates.length));
    const items = rates
      .map((rate) => {
        const price = rate.price === "0.00" ? this.strings.shippingFree : this.formatMoney(rate.price);
        return `<li><strong>${this.escape(rate.name)}:</strong> ${price}</li>`;
      })
      .join("");
    this.shippingResult.innerHTML = `<p>${intro}</p><ul class="wt-cart-features__rates">${items}</ul>`;
    this.shippingResult.dataset.state = "success";
    this.markInvalid(this.shippingZip, "success");
  }

  renderShippingResult(message, state) {
    this.shippingResult.textContent = message;
    this.shippingResult.dataset.state = state;
    this.markInvalid(this.shippingZip, state);
  }

  formatMoney(stringAmount) {
    // The shipping rates endpoints return `price` in the shop's settlement
    // (base) currency — e.g. "5.00" for a 5 PLN rate on a PLN shop. On a
    // multi-currency store the customer browses in a presentment currency
    // (`Shopify.currency.active`, e.g. "ALL"), and `Shopify.currency.rate`
    // holds the conversion factor from base → presentment.
    //
    // To match the rest of the cart UI (which shows everything in the
    // customer's presentment currency), convert the rate to presentment
    // and format with the active currency code.
    //   shop=PLN, customer=ALL, rate=21.5 → 5.00 PLN displayed as 107.50 L
    //
    // Falls through gracefully:
    //   - no presentment set → format the base amount with `shopCurrency`
    //   - missing or 1.0 rate → no conversion, format with whichever
    //     currency code is available
    const baseAmount = parseFloat(stringAmount);
    const presentment = window.Shopify?.currency?.active || "";
    const rate = parseFloat(window.Shopify?.currency?.rate || "1") || 1;
    const baseCode = this.shopCurrency || presentment || "USD";

    let amount = baseAmount;
    let code = baseCode;
    if (presentment && rate && rate !== 1) {
      amount = baseAmount * rate;
      code = presentment;
    } else if (presentment) {
      // Same currency on both sides (or rate not provided) — use
      // presentment so the code matches the rest of the storefront.
      code = presentment;
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
      }).format(amount);
    } catch (e) {
      return `${amount.toFixed(2)} ${code}`;
    }
  }

  escape(str) {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  // ── Discount code ───────────────────────────────────────────
  initDiscount() {
    const applyBtn = this.querySelector("[data-discount-apply]");
    if (!applyBtn) return;

    this.discountInput = this.querySelector("[data-discount-input]");
    this.discountResult = this.querySelector("[data-discount-result]");
    this.applyBtn = applyBtn;

    applyBtn.addEventListener("click", this.onDiscountApply.bind(this));
    this.bindDiscountRemoves();

    this.discountInput?.addEventListener("input", () => {
      this.discountInput.removeAttribute("aria-invalid");
    });
  }

  bindDiscountRemoves() {
    this.querySelectorAll("[data-discount-remove-code]").forEach((btn) => {
      btn.addEventListener("click", () => this.onDiscountRemoveCode(btn.dataset.discountRemoveCode, btn));
    });
  }

  // A drawer refresh on /cart only needs the page chips updated. Replacing the
  // page element would collapse an open note or shipping estimate.
  syncDiscountCodes(incomingPanel) {
    this.dataset.discountCodes = incomingPanel.dataset.discountCodes || "";
    const incomingList = incomingPanel.querySelector(".wt-cart-features__discount-list");
    const existingList = this.querySelector(".wt-cart-features__discount-list");
    if (!incomingList) {
      existingList?.remove();
      return;
    }
    const nextList = document.importNode(incomingList, true);
    if (existingList) existingList.replaceWith(nextList);
    else this.querySelector('[data-drawer="discount"] .wt-cart-features__drawer-body')?.prepend(nextList);
    this.bindDiscountRemoves();
  }

  async onDiscountApply() {
    const code = (this.discountInput?.value || "").trim();
    if (!code) {
      this.renderDiscountResult(this.strings.discountEnter, "error");
      return;
    }

    this.applyBtn.disabled = true;
    this.renderDiscountResult(this.strings.discountApplying, "loading");

    try {
      const cartBefore = await fetch(`${window.Shopify.routes.root}cart.js`).then((r) => r.json());

      const existingCodes = (cartBefore?.discount_codes ?? []).map(el => el.code);
      const appliedCodes = [...existingCodes, code];
      const codesString = appliedCodes.join(',')

      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ discount: codesString }),
      });

      const cartAfter = await fetch(`${window.Shopify.routes.root}cart.js`).then((r) => r.json());

      if (this.isDiscountApplied(cartAfter, code, cartBefore)) {
        this.renderDiscountResult(this.strings.discountApplied, "success");
        rememberPanel({ context: this.context, feature: "discount", message: this.strings.discountApplied });
        this.refreshCart();
      } else {
        this.renderDiscountResult(this.strings.discountInvalid, "error");
      }
    } catch (err) {
      console.error("[cart-features discount apply]", err);
      this.renderDiscountResult(this.strings.discountError, "error");
    } finally {
      this.applyBtn.disabled = false;
    }
  }

  isDiscountApplied(cartAfter, code, cartBefore) {
    const normalCode = code.trim().toLowerCase();
    const apps = cartAfter.cart_level_discount_applications || [];
    if (apps.some((a) => a.title && a.title.toLowerCase() === normalCode)) return true;
    if (cartAfter.total_discount > (cartBefore.total_discount || 0)) return true;
    if (apps.length > (cartBefore.cart_level_discount_applications || []).length) return true;
    return false;
  }

  async onDiscountRemoveCode(codeToRemove, btn) {
    if (btn) {
      btn.disabled = true;
      btn.dataset.loading = "";
    }

    try {
      const remaining = (this.dataset.discountCodes || "")
        .split(",")
        .map((c) => c.trim())
        .filter((c) => c && c.toLowerCase() !== codeToRemove.trim().toLowerCase())
        .join(',');

      // Clear all discounts via cart update, then re-apply remaining ones
      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ discount: '' }),
      });

      await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ discount: remaining }),
      });


      this.refreshCart();
      this.closeAll();
    } catch (err) {
      console.error("[cart-features discount remove]", err);
    } finally {
      if (btn) {
        btn.disabled = false;
        delete btn.dataset.loading;
      }
    }
  }

  renderDiscountResult(message, state) {
    if (!this.discountResult) return;
    this.discountResult.textContent = message;
    this.discountResult.dataset.state = state;
    this.markInvalid(this.discountInput, state);
  }

  // The result region is announced, but the field itself must carry the state too.
  markInvalid(field, state) {
    if (!field) return;
    if (state === "error") {
      field.setAttribute("aria-invalid", "true");
    } else {
      field.removeAttribute("aria-invalid");
    }
  }

  async refreshCart() {
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer && typeof cartDrawer.refreshCartDrawer === "function") {
      cartDrawer.refreshCartDrawer();
    }

    // 2. Cart page — fetch fresh `main-cart-items` + `main-cart-footer`
    //    + `cart-icon-bubble` sections and swap their `.js-contents` /
    //    `.shopify-section` regions. Without this the cart page would
    //    stay stale (subtotal, savings, line discounts) after applying
    //    or removing a discount code on the page.
    const cartItemsEl = document.getElementById("main-cart-items");
    const cartFooterEl = document.getElementById("main-cart-footer");

    if (cartItemsEl || cartFooterEl) {
      const sectionIds = [];
      if (cartItemsEl?.dataset.id) sectionIds.push(cartItemsEl.dataset.id);
      if (cartFooterEl?.dataset.id) sectionIds.push(cartFooterEl.dataset.id);
      sectionIds.push("cart-icon-bubble");

      try {
        const response = await fetch(
          `${window.Shopify.routes.root}?sections=${sectionIds.join(",")}`,
        );
        const data = await response.json();

        const targets = [
          { key: cartItemsEl?.dataset.id, el: cartItemsEl, selector: ".js-contents" },
          { key: cartFooterEl?.dataset.id, el: cartFooterEl, selector: ".js-contents" },
          {
            key: "cart-icon-bubble",
            el: document.getElementById("shopify-section-cart-icon-bubble"),
            selector: ".shopify-section",
          },
        ];

        targets.forEach((target) => {
          if (!target.key || !target.el || !data[target.key]) return;
          const parsed = new DOMParser().parseFromString(data[target.key], "text/html");
          const incoming = parsed.querySelector(target.selector);
          const existing = target.el.querySelector(target.selector);
          if (incoming && existing) {
            existing.innerHTML = incoming.innerHTML;
          }

          // The panel sits outside the footer's `.js-contents`, so its code chips and
          // data-discount-codes stayed stale until reload. The page instance is
          // replaced so `pendingPanel` can reopen it. A drawer instance only
          // copies the chips, leaving the page note and shipping state in place.
          if (target.el === cartFooterEl) {
            const incomingPanel = parsed.querySelector("cart-additional-features");
            const existingPanel = cartFooterEl.querySelector("cart-additional-features");
            if (incomingPanel && existingPanel) {
              if (this.context !== "page") {
                existingPanel.syncDiscountCodes(incomingPanel);
              } else {
                const focusWasInside = existingPanel.contains(document.activeElement);
                const nextPanel = document.importNode(incomingPanel, true);
                existingPanel.replaceWith(nextPanel);
                if (focusWasInside) {
                  nextPanel.querySelector('.wt-cart-features__trigger[data-feature="discount"]')?.focus();
                }
              }
            }
          }
        });
      } catch (err) {
        console.error("[cart-features] section refresh", err);
      }
    }

    // 3. Notify pub/sub subscribers (cart icon bubble, sticky bars, …).
    //    Convention from CLAUDE.md: "JS — Pub/Sub events via constants.js
    //    (PUB_SUB_EVENTS)". Same call as cart-service-checkbox.js.
    if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-additional-features" });
    }
  }
}

if (!customElements.get("cart-additional-features")) {
  customElements.define("cart-additional-features", CartAdditionalFeatures);
}
